import {isArray, isNumber} from "lodash-es"
import {Feature} from "ol"
import {LineString, SimpleGeometry} from "ol/geom";
import {ObjectEvent} from "ol/Object"
import {FeatureLike} from "ol/Feature"
import {Coordinate} from "ol/coordinate"
import {Style} from "ol/style"
import VectorLayer from "ol/layer/Vector"
import {splitLineString} from "@ui/common/utils"
import mapService from "@ui/services/MapService"

type TPositionPoint = {
  point: Coordinate,
  time: number,
  rotation?: number
}

export const animateLineStringFeature = <T extends SimpleGeometry>(
  layer: VectorLayer<Feature<T>>,
  feature: Feature<LineString>,
  style: (point: Coordinate, rotation?: number) => Style | Style[],
  maxTimeAnimation: number = 1000
) => {
  feature.set("type", "animated")
  feature.on("change:geometry", (event: ObjectEvent) => {
      const lineString = feature.getGeometry()?.getCoordinates()
      if (lineString) {
        feature.set("newData",
          {
            point: lineString[lineString.length - 1],
            time: Date.now(),
            rotation: feature.getGeometry()?.get("rotation")
          },
          true)
      }
    })
  layer.setStyle((feature: FeatureLike, resolution: number) => {
    if (feature.get("type") === "animated") {
      feature = feature as Feature<LineString>
      const newData: TPositionPoint = feature.get("newData") // новая точка
      const current: TPositionPoint = feature.get("current") // текущая точка к ней движемся или дошли
      let splitPoints: Coordinate[] = feature.get("splitPoints")      // разбитая LineString на массив точек, для анимации движения
      const index = feature.get("index")                // текущий индекс координаты из массива splitPoints в процессе анимации
      const intervalId = feature.get("intervalId")      // id метода setInterval

      if (!newData && !current) { // нет данных
        return []
      }
      if (newData) {

        if (!current) { // появилась первая точка
          feature.set("current", newData, true)
          return style(newData.point, newData.rotation)
        }

        if (newData.point[0] === current.point[0] && newData.point[1] === current.point[1]) { // новой точки нет, обрабатываем старые данные
          if (isArray(splitPoints) && isNumber(index) && index > -1) {
            if (splitPoints.length > index) { // продолжаем анимацию
              return style(splitPoints[index], current.rotation)
            } else { // закончили анимацию
              clearInterval(intervalId)
              feature.set("splitPoints", undefined, true)
              feature.set("index", -1, true)
            }
          }
          // без анимации показываем кораблик в конечной точке
          const lineString = (feature.getGeometry() as LineString).getCoordinates()
          const lastPoint = lineString[lineString.length - 1]
          return style(lastPoint, current.rotation)
        } else { // появились новые данные
          clearInterval(intervalId)

          let lastPoint = {...current}
          if (isArray(splitPoints) && isNumber(index) && index > -1) {
            if (splitPoints.length > index) {
              // не закончили старую анимацию, просчитываем новую анимацию из точки в которой сейчас находится анимация
              lastPoint = {...lastPoint, point: splitPoints[index]}
            }
          }

          const lineString = new LineString([lastPoint.point, newData.point])
          splitPoints = splitLineString(lineString, resolution, {alwaysUp: false, midPoints: true})
          // расчитаем LineString по диагонали экрана
          const [width, height] = mapService.getMapSize() || []
          const screenDiagonal = new LineString([
            mapService.getCoordinateFromPixel([0, height]),
            mapService.getCoordinateFromPixel([width, 0])
          ])
          // если анимируем фичу, которая движется из-за экрана,
          // то берем только последние точки из массива splitPoints
          // расположенные в районе экрана
          if (screenDiagonal.getLength() < lineString.getLength()) {
            const ratio = 1 - (screenDiagonal.getLength() * 2 / lineString.getLength())
            splitPoints = splitPoints.splice(Math.floor(splitPoints.length * ratio))
          }

          feature.set("index", 0, true)
          feature.set("splitPoints", splitPoints, true)
          feature.set("intervalId", setInterval(
            () =>  (feature as Feature).set("index", feature.get("index") + 1),
            Math.min(newData.time - lastPoint.time, maxTimeAnimation) / (splitPoints.length + 1)
          ))
          feature.set("current", newData)

          return style(lastPoint.point, newData.rotation)
        }
      }
    }
  })
}
